/**
 * Website Integration Tab Component
 * Manages website integration and platform connection
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Laptop, 
  Globe, 
  Settings, 
  CheckCircle, 
  AlertCircle,
  Save,
  Eye,
  EyeOff,
  ExternalLink
} from 'lucide-react';
import { SuperAdminOnboardingData, getStatusBadgeVariant, getStatusDisplayName } from '@/types/super-admin-onboarding';
import { OnboardingStatus, SetupMethod } from '@/types/onboarding';
import { toast } from '@/hooks/use-toast';

interface WebsiteIntegrationTabProps {
  data: SuperAdminOnboardingData;
  onFieldUpdate: (field: keyof SuperAdminOnboardingData, value: any) => Promise<void>;
  onBulkFieldUpdate?: (updates: Record<string, any>) => Promise<{ success: boolean; changedCount: number }>;
  onStatusUpdate: (status: OnboardingStatus) => Promise<void>;
}

const WebsiteIntegrationTab: React.FC<WebsiteIntegrationTabProps> = ({
  data,
  onFieldUpdate,
  onBulkFieldUpdate,
  onStatusUpdate
}) => {
  const [formData, setFormData] = useState({
    website_setup_method: data.website_setup_method || SetupMethod.REMOTE_ACCESS,
    website_login_username: data.website_login_username || '',
    website_login_password: data.website_login_password || '',
    website_platform: data.website_platform || '',
    website_platform_custom: data.website_platform_custom || ''
  });
  
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPassword, setShowPassword] = useState(true); // Default to showing password

  // Common website platforms
  const websitePlatforms = [
    { value: 'squarespace', label: 'Squarespace' },
    { value: 'wix', label: 'Wix' },
    { value: 'wordpress', label: 'WordPress' },
    { value: 'webflow', label: 'Webflow' },
    { value: 'custom', label: 'Custom/Other' }
  ];

  // Handle form field changes
  const handleFieldChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  // Save all changes
  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (onBulkFieldUpdate) {
        // Use the new bulk update function
        const result = await onBulkFieldUpdate(formData);
        
        if (result.changedCount === 0) {
          toast({
            title: "No Changes",
            description: "No fields were modified"
          });
        } else {
          toast({
            title: "Success",
            description: `Updated ${result.changedCount} field(s) successfully`
          });
        }
      } else {
        // Fallback to individual updates if bulk update not available
        const changedFields = Object.entries(formData).filter(([field, value]) => {
          const originalValue = data[field as keyof SuperAdminOnboardingData];
          return originalValue !== value;
        });
        
        if (changedFields.length === 0) {
          toast({
            title: "No Changes",
            description: "No fields were modified"
          });
          return;
        }
        
        const updates = changedFields.map(([field, value]) => 
          onFieldUpdate(field as keyof SuperAdminOnboardingData, value)
        );
        
        await Promise.all(updates);
        
        toast({
          title: "Success",
          description: `Updated ${changedFields.length} field(s) successfully`
        });
      }
      
      setHasChanges(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save website integration information",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  // Update status
  const handleStatusUpdate = async (status: OnboardingStatus) => {
    try {
      // Save changes first if any
      if (hasChanges) {
        await handleSave();
      }
      
      await onFieldUpdate('website_linked_status', status);
      
      toast({
        title: "Success",
        description: `Website integration status updated to ${getStatusDisplayName(status)}`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  const isComplete = data.website_linked_status === OnboardingStatus.COMPLETE;
  const hasRequiredInfo = formData.website_setup_method === SetupMethod.DIY || 
                         (formData.website_platform && 
                          (formData.website_platform !== 'custom' || formData.website_platform_custom) &&
                          formData.website_login_username && formData.website_login_password);

  return (
    <div className="space-y-6">
      {/* Header with Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Laptop className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle>Website Integration</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Connect the ordering system with existing website
                </p>
              </div>
            </div>
            <Badge variant={getStatusBadgeVariant(data.website_linked_status || OnboardingStatus.NOT_STARTED)}>
              {getStatusDisplayName(data.website_linked_status || OnboardingStatus.NOT_STARTED)}
            </Badge>
          </div>
        </CardHeader>
      </Card>


      {/* Setup Method */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Setup Method
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Website Integration Method</Label>
            <Select
              value={formData.website_setup_method}
              onValueChange={(value) => handleFieldChange('website_setup_method', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select setup method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SetupMethod.REMOTE_ACCESS}>Remote Access Integration</SelectItem>
                <SelectItem value={SetupMethod.DIY}>Customer Self-Integration</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.website_setup_method === SetupMethod.REMOTE_ACCESS ? (
            <div className="p-4 bg-blue-50 dark:bg-blue-950/50 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Remote Access Integration:</strong> Our development team will handle the website integration with the customer's admin access credentials.
              </p>
            </div>
          ) : (
            <div className="p-4 bg-green-50 dark:bg-green-950/50 rounded-lg border border-green-200 dark:border-green-800">
              <p className="text-sm text-green-800 dark:text-green-200">
                <strong>Customer Self-Integration:</strong> The customer will integrate the ordering system themselves using provided code snippets and documentation.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Website Platform */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Website Platform
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="website_platform">Website Platform</Label>
            <Select
              value={formData.website_platform}
              onValueChange={(value) => handleFieldChange('website_platform', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select website platform" />
              </SelectTrigger>
              <SelectContent>
                {websitePlatforms.map(platform => (
                  <SelectItem key={platform.value} value={platform.value}>
                    {platform.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {formData.website_platform === 'custom' && (
            <div className="space-y-2">
              <Label htmlFor="website_platform_custom">Custom Platform Name</Label>
              <Input
                id="website_platform_custom"
                value={formData.website_platform_custom}
                onChange={(e) => handleFieldChange('website_platform_custom', e.target.value)}
                placeholder="Enter custom platform name"
              />
            </div>
          )}

          {formData.website_platform && (
            <div className="p-4 bg-muted/50 rounded-lg">
              <h4 className="font-medium mb-2">Platform-Specific Information</h4>
              {formData.website_platform === 'wordpress' && (
                <p className="text-sm text-muted-foreground">
                  WordPress integration typically involves installing a plugin or adding custom code to your theme files.
                </p>
              )}
              {formData.website_platform === 'shopify' && (
                <p className="text-sm text-muted-foreground">
                  Shopify integration can be done through custom liquid templates or embedded widgets.
                </p>
              )}
              {formData.website_platform === 'wix' && (
                <p className="text-sm text-muted-foreground">
                  Wix integration uses HTML iframe embed codes in your page builder.
                </p>
              )}
              {formData.website_platform === 'squarespace' && (
                <p className="text-sm text-muted-foreground">
                  Squarespace integration uses code injection or embed blocks.
                </p>
              )}
              {(formData.website_platform === 'custom' || formData.website_platform === 'weebly') && (
                <p className="text-sm text-muted-foreground">
                  Custom integration may require specific implementation based on your platform's capabilities.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Website Access (for Remote Setup) */}
      {formData.website_setup_method === SetupMethod.REMOTE_ACCESS && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Website Admin Access
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-amber-50 dark:bg-amber-950/50 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Security Note:</strong> Admin credentials are used only for integration setup and are stored securely. Access is removed after completion.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="website_login_username">Admin Username/Email</Label>
                <Input
                  id="website_login_username"
                  value={formData.website_login_username}
                  onChange={(e) => handleFieldChange('website_login_username', e.target.value)}
                  placeholder="admin@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="website_login_password">Admin Password</Label>
                <div className="relative">
                  <Input
                    id="website_login_password"
                    type={showPassword ? "text" : "password"}
                    value={formData.website_login_password}
                    onChange={(e) => handleFieldChange('website_login_password', e.target.value)}
                    placeholder="••••••••"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>

            {formData.website_login_username && formData.website_login_password && (
              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2">Access Information Summary</h4>
                <div className="text-sm space-y-1">
                  <div>Platform: {websitePlatforms.find(p => p.value === formData.website_platform)?.label || 'Not specified'}</div>
                  <div>Username: {formData.website_login_username}</div>
                  <div>Password: {showPassword ? formData.website_login_password : '••••••••'}</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Integration Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Integration Process</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {formData.website_setup_method === SetupMethod.REMOTE_ACCESS ? (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  1
                </div>
                <div>
                  <h4 className="font-medium">Access Verification</h4>
                  <p className="text-sm text-muted-foreground">
                    Verify admin access to the customer's website platform.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  2
                </div>
                <div>
                  <h4 className="font-medium">Integration Implementation</h4>
                  <p className="text-sm text-muted-foreground">
                    Implement the ordering system integration based on the platform type.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  3
                </div>
                <div>
                  <h4 className="font-medium">Styling & Customisation</h4>
                  <p className="text-sm text-muted-foreground">
                    Apply custom styling to match the restaurant's brand and website design.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  4
                </div>
                <div>
                  <h4 className="font-medium">Testing & Go-Live</h4>
                  <p className="text-sm text-muted-foreground">
                    Test the integration thoroughly and make it live for customers.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  1
                </div>
                <div>
                  <h4 className="font-medium">Receive Integration Code</h4>
                  <p className="text-sm text-muted-foreground">
                    Customer receives platform-specific integration code and documentation.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  2
                </div>
                <div>
                  <h4 className="font-medium">Implement Integration</h4>
                  <p className="text-sm text-muted-foreground">
                    Follow the provided instructions to add the ordering system to their website.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  3
                </div>
                <div>
                  <h4 className="font-medium">Customize Appearance</h4>
                  <p className="text-sm text-muted-foreground">
                    Adjust styling and positioning to match their website design.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  4
                </div>
                <div>
                  <h4 className="font-medium">Test & Launch</h4>
                  <p className="text-sm text-muted-foreground">
                    Test the integration with sample orders before going live.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={handleSave} 
              disabled={!hasChanges || saving}
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            
            {hasRequiredInfo && !isComplete && (
              <>
                <Button 
                  variant="default"
                  onClick={() => handleStatusUpdate(OnboardingStatus.AWAITING_CONFIRMATION)}
                >
                  Mark as Ready for Integration
                </Button>
                
                <Button 
                  variant="default"
                  onClick={() => handleStatusUpdate(OnboardingStatus.COMPLETE)}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark as Complete
                </Button>
              </>
            )}
            
            <Button 
              variant="outline"
              onClick={() => handleStatusUpdate(OnboardingStatus.NEEDS_ATTENTION)}
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Mark as Needs Attention
            </Button>
          </div>
          
          <div className="p-4 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">
              {!hasRequiredInfo 
                ? "Select the website platform and provide required information to proceed with integration."
                : formData.website_setup_method === SetupMethod.REMOTE_ACCESS
                ? "Platform and access details are configured. Ready for remote integration setup."
                : "Platform configured. Customer can proceed with self-guided integration using provided documentation."
              }
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WebsiteIntegrationTab;